import React, { useMemo } from 'react';

import { ItemValidator } from '../../../../../services/ItemValidator';
import { DeselectedMenuItemAction, SelectedHotspot } from '../../../../actions/menuActions';
import { getItemByIdTypedSelector } from '../../../../store/reducers/items/itemsSelectors';
import { useSelectItem } from '../../../../store/reducers/items/useSelectItem';
import { noTrackedItemsSelector } from '../../../../store/reducers/student/tracking/itemsTrackingSelectors';
import { getCurrentItem } from '../../../../store/selectors/getCurrentItem';
import { getMenuContentItemsSelector } from '../../../../store/selectors/getMenuContentItems';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { userProgressServiceSelector } from '../../../tracking/services/UserProgressService';
import { ItemDetails, ItemDetailsType, MenuItem, MenuItemProps } from '../../components/student/MenuItem';
import { isHotspot, NonSequencedFlowItem as NonSequencedFlowItemModel, TrackingStatus } from '@ael/core-api';
import { createCompletedOfTotalProgressDisplayConfiguration } from '@ael/shared-components-library';
import { useDispatch } from 'react-redux';
import {useIsMobileV2} from "../../../../hooks/useIsMobile";

interface NonSequencedFlowItemOwnProps {
  item: NonSequencedFlowItemModel;
  trackingStatus?: TrackingStatus;
  children?: React.ReactNode;
  parentItemId: string | null;
  nestedLevel: number;
  isLinearModeOn: boolean;
}

export const NonSequencedFlowItem: React.FC<NonSequencedFlowItemOwnProps> = ({
  item,
  trackingStatus,
  children,
  parentItemId,
  nestedLevel,
  isLinearModeOn,
}) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobileV2();
  const { goToItem } = useSelectItem();
  const currentItem = useStudentSelector(getCurrentItem);
  const getItemByIdTyped = useStudentSelector(getItemByIdTypedSelector);
  const noTrackedItems = useStudentSelector(noTrackedItemsSelector);
  const getMenuContentItems = useStudentSelector(getMenuContentItemsSelector);
  const progressResult = useStudentSelector(userProgressServiceSelector).evaluate(item);
  const hotspots = useMemo(
    () =>
      item.hotspots
        .map((itemId: string) => {
          const hotspot = getItemByIdTyped(itemId, isHotspot);
          return hotspot;
        })
        .toList(),
    [item.hotspots, getItemByIdTyped]
  );
  const isItemMandatoryOrCompletionDisabled = noTrackedItems || item.isMandatory;

  const isSelected = currentItem === item;
  const disabled = !ItemValidator.isValid(item);
  const contentItems = getMenuContentItems(item.id);
  const progress = progressResult.itemsCount
    ? createCompletedOfTotalProgressDisplayConfiguration(progressResult.itemsCount, progressResult.completedItemsCount)
    : undefined;
  const showItemCompletion = isItemMandatoryOrCompletionDisabled;
  const stateProps = useMemo(
    () => ({
      disabled,
      isSelected,
      trackingStatus,
      hotspots,
      contentItems,
      progress,
      showItemCompletion,
    }),
    [isSelected, disabled, trackingStatus, hotspots, contentItems, progress, showItemCompletion]
  );

  const dispatchProps = useMemo(
    () => ({
      onClick: () => {
        goToItem(item.id);
      },
      onMenuHotspotClick: (hotspotId: string) => {
        dispatch(SelectedHotspot(hotspotId, isMobile));
      },
      onDeselectMenuItem: () => {
        dispatch(DeselectedMenuItemAction(item));
      },
    }),
    [dispatch, goToItem, item, isMobile]
  );

  const menuItemDetails: ItemDetails = useMemo(
    () => ({
      itemDetailsType: ItemDetailsType.Item,
      active: stateProps.isSelected,
      trackingStatus: stateProps.trackingStatus,
      disabled: stateProps.disabled,
      contentItems: stateProps.contentItems,
      parentItemId: parentItemId,
      progress: stateProps.progress,
      showItemCompletion: stateProps.showItemCompletion,
      nestedLevel: nestedLevel,
      itemId: item.id,
    }),
    [
      item.id,
      nestedLevel,
      parentItemId,
      stateProps.contentItems,
      stateProps.disabled,
      stateProps.isSelected,
      stateProps.progress,
      stateProps.showItemCompletion,
      stateProps.trackingStatus,
    ]
  );

  const mergeProps: MenuItemProps = {
    children: children,
    menuItemDetails,
    isLinearModeOn: isLinearModeOn,
    onClick: stateProps.isSelected
      ? dispatchProps.onDeselectMenuItem
      : () => {
          dispatchProps.onClick();
        },
    onMenuContentItemClick: dispatchProps.onMenuHotspotClick,
  };

  return <MenuItem {...mergeProps} />;
};
